import React, { useMemo, useRef } from 'react';

import { Empty } from 'antd';
import classNames from 'classnames';

import { useHasVerticalScrollbar } from '@/shared/hooks';
import { highlightText, matchesKeywordFilter } from '@/shared/lib/text';
import { formatMetricValue, parseFiniteNumberString } from '@/shared/lib/utils/number';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import { ExperimentAssetTabShell } from '@/widgets/experiment-asset-detail/ExperimentAssetTabShell';

import styles from './ParametersTab.module.css';

function getParamValueDisplayString(value: string): string {
  const n = parseFiniteNumberString(value);
  return n !== null ? formatMetricValue(n) : value;
}

type ParametersTabProps = {
  params: Array<{ key: string; value: string }>;
  shellVariant?: 'panel' | 'sidebar';
};

type ParametersTabBodyProps = {
  params: Array<{ key: string; value: string }>;
  searchQuery: string;
  shellVariant: 'panel' | 'sidebar';
};

const ParametersTabBody: React.FC<ParametersTabBodyProps> = ({ params, searchQuery, shellVariant }) => {
  const filteredParams = useMemo(
    () =>
      params.filter((param) => {
        const displayValue = getParamValueDisplayString(param.value);
        return matchesKeywordFilter(searchQuery, param.key, param.value, displayValue);
      }),
    [params, searchQuery]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const { hasVerticalScroll, scrollbarWidth } = useHasVerticalScrollbar(scrollRef, [filteredParams]);
  const useScrollGutter = shellVariant === 'panel' && hasVerticalScroll;

  const infoSectionStyle = (useScrollGutter ? { '--scrollbar-width': `${scrollbarWidth}px` } : undefined) as
    | React.CSSProperties
    | undefined;

  return (
    <div
      className={classNames(styles.parametersTabInner, shellVariant === 'sidebar' && styles.parametersTabInnerSidebar)}>
      {filteredParams.length > 0 ? (
        <div className={styles.infoSection} style={infoSectionStyle}>
          <div
            className={classNames(styles.infoHeader, {
              [styles.scrollGutterRight]: useScrollGutter,
            })}>
            <span className={styles.infoHeaderLabel}>Parameter</span>
            <span className={styles.infoHeaderLabel}>Value</span>
          </div>
          <div
            ref={scrollRef}
            className={classNames(styles.infoRowsScroll, {
              [styles.scrollGutterRight]: useScrollGutter,
            })}>
            {filteredParams.map((param) => {
              const displayValue = getParamValueDisplayString(param.value);

              return (
                <div key={param.key} className={styles.infoRow}>
                  <span className={styles.infoLabel}>
                    <EllipsisText text={param.key} tooltipPlacement="topLeft">
                      {highlightText(param.key, searchQuery, { className: 'highlight' })}
                    </EllipsisText>
                  </span>
                  <span className={styles.infoValue}>
                    <EllipsisText text={String(displayValue)} tooltipPlacement="topRight">
                      {highlightText(String(displayValue), searchQuery, { className: 'highlight' })}
                    </EllipsisText>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
      )}
    </div>
  );
};

export const ParametersTab: React.FC<ParametersTabProps> = ({ params, shellVariant = 'panel' }) => {
  return (
    <ExperimentAssetTabShell variant={shellVariant}>
      {({ searchQuery }) => <ParametersTabBody params={params} searchQuery={searchQuery} shellVariant={shellVariant} />}
    </ExperimentAssetTabShell>
  );
};
